/**
 * Datos completos de productos Horeca
 * Incluye descripciones, ingredientes, formatos e información detallada
 *
 * Los textos traducibles son objetos `{ es, en, fr, pt }` (ver
 * `i18n/localizeContent`); los ids, slugs, imágenes, nombres de producto y
 * formatos son valores simples. El texto de `formatDisplay`/`formats` se
 * localiza al mostrarlo (`components/products/productFormat`).
 * Los ingredientes se traducen fielmente (etiquetado legal): no reescribir.
 */

const INGREDIENTES_KETCHUP = {
  es: 'Concentrado de tomate, azúcar, vinagre, sal, almidón modificado de maíz, estabilizantes (goma guar, goma garrofín, pectinas), conservador (benzoato sódico) y aromas.',
  en: 'Tomato concentrate, sugar, vinegar, salt, modified maize starch, stabilisers (guar gum, locust bean gum, pectins), preservative (sodium benzoate) and flavourings.',
  fr: 'Concentré de tomate, sucre, vinaigre, sel, amidon modifié de maïs, stabilisants (gomme guar, gomme de caroube, pectines), conservateur (benzoate de sodium) et arômes.',
  pt: 'Concentrado de tomate, açúcar, vinagre, sal, amido modificado de milho, estabilizadores (goma guar, goma de alfarroba, pectinas), conservante (benzoato de sódio) e aromas.',
};

const INGREDIENTES_MOSTAZA_SULFITOS = {
  es: 'Agua, azúcar, vinagre de alochol, harina de mostaza, estabilizantes (goma guar goma xantana y almidón modificado de maíz), sal, aceite de girasol, conservador (metabisulfito potásico y sorbato potásico) colorante (caramelo) y especias. Contiene dióxido de azufre y sulfitos en concentraciones superiores a 10mg/kg.',
  en: 'Water, sugar, spirit vinegar, mustard flour, stabilisers (guar gum, xanthan gum and modified maize starch), salt, sunflower oil, preservatives (potassium metabisulphite and potassium sorbate), colour (caramel) and spices. Contains sulphur dioxide and sulphites at concentrations above 10mg/kg.',
  fr: 'Eau, sucre, vinaigre d’alcool, farine de moutarde, stabilisants (gomme guar, gomme xanthane et amidon modifié de maïs), sel, huile de tournesol, conservateurs (métabisulfite de potassium et sorbate de potassium), colorant (caramel) et épices. Contient du dioxyde de soufre et des sulfites en concentrations supérieures à 10\u00a0mg/kg.',
  pt: 'Água, açúcar, vinagre de álcool, farinha de mostarda, estabilizadores (goma guar, goma xantana e amido modificado de milho), sal, óleo de girassol, conservantes (metabissulfito de potássio e sorbato de potássio), corante (caramelo) e especiarias. Contém dióxido de enxofre e sulfitos em concentrações superiores a 10\u00a0mg/kg.',
};

const INGREDIENTES_BARBACOA = {
  es: 'Concentrado de tomate, azúcar, jarabe de melaza, vinagre de alcohol, sal, almidón modificado de maíz, aroma de humo, estabilizante (goma guar), conservador (benzoato sódico), especias y aromas.',
  en: 'Tomato concentrate, sugar, molasses syrup, spirit vinegar, salt, modified maize starch, smoke flavouring, stabiliser (guar gum), preservative (sodium benzoate), spices and flavourings.',
  fr: 'Concentré de tomate, sucre, sirop de mélasse, vinaigre d’alcool, sel, amidon modifié de maïs, arôme de fumée, stabilisant (gomme guar), conservateur (benzoate de sodium), épices et arômes.',
  pt: 'Concentrado de tomate, açúcar, xarope de melaço, vinagre de álcool, sal, amido modificado de milho, aroma de fumo, estabilizador (goma guar), conservante (benzoato de sódio), especiarias e aromas.',
};

const INGREDIENTES_ALIOLI = {
  es: 'Aceite de girasol (64%), agua, huevo y yema de huevo(6,5%), ajo natural (4,8%), vinagre de vino blanco, azúcar, perejil, sal, almidón modificado de maíz, estabilizante (goma garrofín), zumo de limón, conservador (sorbato potásico), mostaza, acidulante (ácido cítrico), antioxidante (EDTA), colorante (betacaroteno) y aromas.',
  en: 'Sunflower oil (64%), water, egg and egg yolk (6.5%), natural garlic (4.8%), white wine vinegar, sugar, parsley, salt, modified maize starch, stabiliser (locust bean gum), lemon juice, preservative (potassium sorbate), mustard, acidifier (citric acid), antioxidant (EDTA), colour (beta-carotene) and flavourings.',
  fr: 'Huile de tournesol (64\u00a0%), eau, œuf et jaune d’œuf (6,5\u00a0%), ail naturel (4,8\u00a0%), vinaigre de vin blanc, sucre, persil, sel, amidon modifié de maïs, stabilisant (gomme de caroube), jus de citron, conservateur (sorbate de potassium), moutarde, acidifiant (acide citrique), antioxydant (EDTA), colorant (bêta-carotène) et arômes.',
  pt: 'Óleo de girassol (64%), água, ovo e gema de ovo (6,5%), alho natural (4,8%), vinagre de vinho branco, açúcar, salsa, sal, amido modificado de milho, estabilizador (goma de alfarroba), sumo de limão, conservante (sorbato de potássio), mostarda, acidificante (ácido cítrico), antioxidante (EDTA), corante (betacaroteno) e aromas.',
};

export const horecaProductsData = {
  // CATEGORÍA 1: FORMATO BIBERÓN
  biberon: [
    {
      id: 'ketchup-biberon',
      name: 'Ketchup Intercasa Biberón',
      shortName: 'Ketchup Biberón',
      category: {
        es: 'Formato Biberón',
        en: 'Squeeze Bottle Format',
        fr: 'Format flacon souple',
        pt: 'Formato frasco doseador',
      },
      categorySlug: 'biberon',
      shortDescription: {
        es: 'Ketchup de tomates seleccionados para hostelería.',
        en: 'Ketchup made from selected tomatoes for the hospitality trade.',
        fr: 'Ketchup de tomates sélectionnées pour l’hôtellerie-restauration.',
        pt: 'Ketchup de tomates selecionados para hotelaria e restauração.',
      },
      description: {
        es: 'Ketchup elaborado con tomates seleccionados y un sabor equilibrado, perfecto para el ritmo diario de cocinas profesionales y servicios de hostelería.',
        en: 'Ketchup made from carefully selected tomatoes with a balanced flavour, perfect for the daily pace of professional kitchens and hospitality services.',
        fr: 'Ketchup élaboré avec des tomates sélectionnées et une saveur équilibrée, parfait pour le rythme quotidien des cuisines professionnelles et des services d’hôtellerie-restauration.',
        pt: 'Ketchup elaborado com tomates selecionados e um sabor equilibrado, perfeito para o ritmo diário das cozinhas profissionais e dos serviços de hotelaria e restauração.',
      },
      ingredients: INGREDIENTES_KETCHUP,
      formats: ['Biberón 900g'],
      formatDisplay: 'Biberón 900g',
      idealFor: {
        es: 'Restaurantes, bares, cafeterías, servicios de comida rápida y establecimientos que necesitan un formato práctico y listo para servir.',
        en: 'Restaurants, bars, cafés, fast-food outlets and establishments that need a practical, ready-to-serve format.',
        fr: 'Restaurants, bars, cafés, restauration rapide et établissements qui recherchent un format pratique, prêt à servir.',
        pt: 'Restaurantes, bares, cafés, serviços de comida rápida e estabelecimentos que precisam de um formato prático e pronto a servir.',
      },
      productType: 'Horeca',
      image: '/images/productos/HORECA/Ketchup_biberón_900gr.webp',
      bannerImage: '/images/productos/BANNERS IDEAL PARA/ketchup-normal-zero.webp'
    },
    {
      id: 'mostaza-dulce-biberon',
      name: 'Mostaza Dulce Biberón',
      shortName: 'Mostaza Dulce Biberón',
      category: {
        es: 'Formato Biberón',
        en: 'Squeeze Bottle Format',
        fr: 'Format flacon souple',
        pt: 'Formato frasco doseador',
      },
      categorySlug: 'biberon',
      shortDescription: {
        es: 'Mostaza dulce suave y equilibrada para hostelería.',
        en: 'Mild, balanced sweet mustard for the hospitality trade.',
        fr: 'Moutarde sucrée douce et équilibrée pour l’hôtellerie-restauration.',
        pt: 'Mostarda doce suave e equilibrada para hotelaria e restauração.',
      },
      description: {
        es: 'Mostaza dulce de textura suave y sabor equilibrado, pensada para acompañar carnes, bocadillos y elaboraciones habituales en hostelería.',
        en: 'Sweet mustard with a smooth texture and balanced flavour, made to accompany meat, sandwiches and dishes typical of the hospitality trade.',
        fr: 'Moutarde sucrée à la texture douce et à la saveur équilibrée, pensée pour accompagner viandes, sandwichs et préparations courantes en hôtellerie-restauration.',
        pt: 'Mostarda doce de textura suave e sabor equilibrado, pensada para acompanhar carnes, sandes e preparações habituais na hotelaria e restauração.',
      },
      ingredients: INGREDIENTES_MOSTAZA_SULFITOS,
      formats: ['Biberón 900g'],
      formatDisplay: 'Biberón 900g',
      idealFor: {
        es: 'Bares, restaurantes, perritos calientes, bocadillos, carnes y platos que buscan un toque amable y ligeramente dulce.',
        en: 'Bars, restaurants, hot dogs, sandwiches, meat and dishes that call for a gentle, slightly sweet touch.',
        fr: 'Bars, restaurants, hot-dogs, sandwichs, viandes et plats qui recherchent une touche délicate et légèrement sucrée.',
        pt: 'Bares, restaurantes, cachorros-quentes, sandes, carnes e pratos que procuram um toque suave e ligeiramente doce.',
      },
      productType: 'Horeca',
      image: '/images/productos/HORECA/Mostaza_dulce_biberón_900g.webp',
      bannerImage: '/images/productos/BANNERS IDEAL PARA/mostaza-dulce.webp'
    },
    {
      id: 'barbacoa-biberon',
      name: 'Salsa Barbacoa Biberón',
      shortName: 'Barbacoa Biberón',
      category: {
        es: 'Formato Biberón',
        en: 'Squeeze Bottle Format',
        fr: 'Format flacon souple',
        pt: 'Formato frasco doseador',
      },
      categorySlug: 'biberon',
      shortDescription: {
        es: 'Salsa ahumada e intensa para carnes y hamburguesas.',
        en: 'Intense, smoky sauce for meat and burgers.',
        fr: 'Sauce fumée et intense pour viandes et hamburgers.',
        pt: 'Molho fumado e intenso para carnes e hambúrgueres.',
      },
      description: {
        es: 'Salsa barbacoa con notas ahumadas y un sabor intenso, ideal para carnes, hamburguesas y recetas donde se busca un acabado con carácter.',
        en: 'Barbecue sauce with smoky notes and an intense flavour, ideal for meat, burgers and recipes where a finish with character is wanted.',
        fr: 'Sauce barbecue aux notes fumées et à la saveur intense, idéale pour les viandes, les hamburgers et les recettes où l’on recherche une finition de caractère.',
        pt: 'Molho barbecue com notas fumadas e um sabor intenso, ideal para carnes, hambúrgueres e receitas em que se procura um acabamento com caráter.',
      },
      ingredients: INGREDIENTES_BARBACOA,
      formats: ['Biberón 900g'],
      formatDisplay: 'Biberón 900g',
      idealFor: {
        es: 'Hamburgueserías, parrillas, cocinas profesionales y locales que requieren una salsa lista para usar con sabor potente.',
        en: 'Burger restaurants, grill houses, professional kitchens and venues that need a ready-to-use sauce with a bold flavour.',
        fr: 'Hamburgeries, grillades, cuisines professionnelles et établissements qui ont besoin d’une sauce prête à l’emploi au goût puissant.',
        pt: 'Hamburguerias, grelhados, cozinhas profissionais e estabelecimentos que precisam de um molho pronto a usar com sabor forte.',
      },
      productType: 'Horeca',
      image: '/images/productos/HORECA/Barbacoa_biberón_900g.webp',
      bannerImage: '/images/productos/BANNERS IDEAL PARA/Barbacoa.webp'
    },
    {
      id: 'alioli-biberon',
      name: 'Alioli Receta Canaria',
      shortName: 'Alioli',
      category: {
        es: 'Formato Biberón',
        en: 'Squeeze Bottle Format',
        fr: 'Format flacon souple',
        pt: 'Formato frasco doseador',
      },
      categorySlug: 'biberon',
      shortDescription: {
        es: 'Alioli de receta canaria en formato biberón para hostelería.',
        en: 'Canarian-recipe alioli in a squeeze bottle for the hospitality trade.',
        fr: 'Alioli de recette canarienne en flacon souple pour l’hôtellerie-restauration.',
        pt: 'Alioli de receita canária em frasco doseador para hotelaria e restauração.',
      },
      description: {
        es: 'Alioli elaborado con ajo natural y perejil siguiendo la receta canaria, con una textura cremosa y un sabor intenso, en un formato biberón práctico y listo para servir.',
        en: 'Alioli (garlic mayonnaise) made with natural garlic and parsley following the Canarian recipe, with a creamy texture and an intense flavour, in a practical squeeze bottle that is ready to serve.',
        fr: 'Alioli (mayonnaise à l’ail) élaboré avec de l’ail naturel et du persil selon la recette canarienne, à la texture crémeuse et à la saveur intense, dans un flacon souple pratique, prêt à servir.',
        pt: 'Alioli (maionese de alho) elaborado com alho natural e salsa segundo a receita canária, com uma textura cremosa e um sabor intenso, num frasco doseador prático e pronto a servir.',
      },
      ingredients: INGREDIENTES_ALIOLI,
      formats: ['Biberón 643ml'],
      formatDisplay: '643ml',
      idealFor: {
        es: 'Asadores, restaurantes, bares, guachinches y locales que buscan un alioli auténtico en un formato práctico de servicio directo.',
        en: 'Grill houses, restaurants, bars, guachinches (traditional Canarian eateries) and venues looking for an authentic alioli in a practical, direct-serve format.',
        fr: 'Rôtisseries, restaurants, bars, guachinches (auberges traditionnelles canariennes) et établissements qui recherchent un alioli authentique dans un format pratique, à servir directement.',
        pt: 'Assadores, restaurantes, bares, guachinches (tascas tradicionais canárias) e estabelecimentos que procuram um alioli autêntico num formato prático de serviço direto.',
      },
      productType: 'Horeca',
      image: '/images/productos/HORECA/Alioli_biberón_643ml.webp',
      imageScale: 0.88,
      bannerImage: '/images/productos/BANNERS IDEAL PARA/Alioli.webp'
    }
  ],

  // CATEGORÍA 2: FORMATO GARRAFA
  garrafa: [
    {
      id: 'ketchup-garrafa',
      name: 'Ketchup Intercasa Garrafa',
      shortName: 'Ketchup Garrafa',
      category: {
        es: 'Formato Garrafa',
        en: 'Jug Format',
        fr: 'Format bidon',
        pt: 'Formato garrafão',
      },
      categorySlug: 'garrafa',
      shortDescription: {
        es: 'Ketchup para cocinas con alto volumen de producción.',
        en: 'Ketchup for kitchens with high production volumes.',
        fr: 'Ketchup pour les cuisines à fort volume de production.',
        pt: 'Ketchup para cozinhas com elevado volume de produção.',
      },
      description: {
        es: 'Ketchup elaborado con tomates seleccionados, pensado para cocinas profesionales que necesitan un producto estable, sabroso y fácil de utilizar en gran volumen.',
        en: 'Ketchup made from selected tomatoes, designed for professional kitchens that need a stable, tasty product that is easy to use in large volumes.',
        fr: 'Ketchup élaboré avec des tomates sélectionnées, conçu pour les cuisines professionnelles qui ont besoin d’un produit stable, savoureux et facile à utiliser en grande quantité.',
        pt: 'Ketchup elaborado com tomates selecionados, pensado para cozinhas profissionais que precisam de um produto estável, saboroso e fácil de utilizar em grande volume.',
      },
      ingredients: INGREDIENTES_KETCHUP,
      formats: ['Garrafa 1.850g', 'Garrafa 5.000g'],
      formatDisplay: 'Garrafa 1.850g | 5.000g',
      idealFor: {
        es: 'Cocinas industriales, comedores, restaurantes, caterings y establecimientos con alto consumo de salsa.',
        en: 'Industrial kitchens, canteens, restaurants, caterers and establishments with high sauce consumption.',
        fr: 'Cuisines industrielles, cantines, restaurants, traiteurs et établissements à forte consommation de sauce.',
        pt: 'Cozinhas industriais, refeitórios, restaurantes, serviços de catering e estabelecimentos com elevado consumo de molho.',
      },
      productType: 'Horeca',
      image: '/images/productos/HORECA/Ketchup_garrafa_1850gr.webp',
      bannerImage: '/images/productos/BANNERS IDEAL PARA/ketchup-normal-zero.webp'
    },
    {
      id: 'mostaza-dulce-garrafa',
      name: 'Mostaza Dulce Garrafa',
      shortName: 'Mostaza Dulce Garrafa',
      category: {
        es: 'Formato Garrafa',
        en: 'Jug Format',
        fr: 'Format bidon',
        pt: 'Formato garrafão',
      },
      categorySlug: 'garrafa',
      shortDescription: {
        es: 'Mostaza dulce para uso profesional en grandes cantidades.',
        en: 'Sweet mustard for professional use in large quantities.',
        fr: 'Moutarde sucrée pour un usage professionnel en grandes quantités.',
        pt: 'Mostarda doce para uso profissional em grandes quantidades.',
      },
      description: {
        es: 'Mostaza dulce con un sabor suave y equilibrado, perfecta para uso profesional en bocadillos, carnes y preparaciones rápidas.',
        en: 'Sweet mustard with a mild, balanced flavour, perfect for professional use in sandwiches, meat and quick preparations.',
        fr: 'Moutarde sucrée à la saveur douce et équilibrée, parfaite pour un usage professionnel dans les sandwichs, les viandes et les préparations rapides.',
        pt: 'Mostarda doce com um sabor suave e equilibrado, perfeita para uso profissional em sandes, carnes e preparações rápidas.',
      },
      ingredients: INGREDIENTES_MOSTAZA_SULFITOS,
      formats: ['Garrafa 1.850g'],
      formatDisplay: 'Garrafa 1.850g',
      idealFor: {
        es: 'Bares, restaurantes, food trucks y negocios que requieren formatos grandes y prácticos.',
        en: 'Bars, restaurants, food trucks and businesses that need large, practical formats.',
        fr: 'Bars, restaurants, food trucks et commerces qui ont besoin de grands formats pratiques.',
        pt: 'Bares, restaurantes, food trucks e negócios que precisam de formatos grandes e práticos.',
      },
      productType: 'Horeca',
      image: '/images/productos/HORECA/Mostaza_dulce_garrafa_1850g.webp',
      bannerImage: '/images/productos/BANNERS IDEAL PARA/mostaza-dulce.webp'
    },
    {
      id: 'barbacoa-garrafa',
      name: 'Salsa Barbacoa Garrafa',
      shortName: 'Barbacoa Garrafa',
      category: {
        es: 'Formato Garrafa',
        en: 'Jug Format',
        fr: 'Format bidon',
        pt: 'Formato garrafão',
      },
      categorySlug: 'garrafa',
      shortDescription: {
        es: 'Barbacoa ahumada para cocinas profesionales.',
        en: 'Smoky barbecue sauce for professional kitchens.',
        fr: 'Sauce barbecue fumée pour cuisines professionnelles.',
        pt: 'Molho barbecue fumado para cozinhas profissionais.',
      },
      description: {
        es: 'Salsa barbacoa de sabor intenso y un toque ahumado, elaborada pensando en cocinas profesionales que buscan consistencia y rendimiento en grandes cantidades.',
        en: 'Barbecue sauce with an intense flavour and a smoky touch, made for professional kitchens that need consistency and good yield in large quantities.',
        fr: 'Sauce barbecue à la saveur intense et à la touche fumée, élaborée pour les cuisines professionnelles qui recherchent régularité et rendement en grandes quantités.',
        pt: 'Molho barbecue de sabor intenso e com um toque fumado, elaborado a pensar nas cozinhas profissionais que procuram consistência e rendimento em grandes quantidades.',
      },
      ingredients: INGREDIENTES_BARBACOA,
      formats: ['Garrafa 1.850g'],
      formatDisplay: 'Garrafa 1.850g',
      idealFor: {
        es: 'Parrillas, hamburgueserías, caterings y locales con alta demanda de salsa barbacoa.',
        en: 'Grill houses, burger restaurants, caterers and venues with high demand for barbecue sauce.',
        fr: 'Grillades, hamburgeries, traiteurs et établissements à forte demande de sauce barbecue.',
        pt: 'Grelhados, hamburguerias, serviços de catering e estabelecimentos com grande procura de molho barbecue.',
      },
      productType: 'Horeca',
      image: '/images/productos/HORECA/Barbacoa_garrafa_1850g.webp',
      bannerImage: '/images/productos/BANNERS IDEAL PARA/Barbacoa.webp'
    }
  ],

  // CATEGORÍA 3: FORMATO CUBO
  cubo: [
    {
      id: 'ketchup-cubo',
      name: 'Ketchup Intercasa Cubo',
      shortName: 'Ketchup Cubo',
      category: {
        es: 'Formato Cubo',
        en: 'Bucket Format',
        fr: 'Format seau',
        pt: 'Formato balde',
      },
      categorySlug: 'cubo',
      shortDescription: {
        es: 'Ketchup para grandes cantidades con rendimiento constante.',
        en: 'Ketchup for large quantities with consistent yield.',
        fr: 'Ketchup pour grandes quantités, au rendement constant.',
        pt: 'Ketchup para grandes quantidades com rendimento constante.',
      },
      description: {
        es: 'Ketchup elaborado con tomates seleccionados y un sabor equilibrado, pensado para cocinas profesionales que requieren grandes cantidades con un rendimiento constante.',
        en: 'Ketchup made from selected tomatoes with a balanced flavour, designed for professional kitchens that need large quantities with a consistent yield.',
        fr: 'Ketchup élaboré avec des tomates sélectionnées et une saveur équilibrée, conçu pour les cuisines professionnelles qui ont besoin de grandes quantités avec un rendement constant.',
        pt: 'Ketchup elaborado com tomates selecionados e um sabor equilibrado, pensado para cozinhas profissionais que precisam de grandes quantidades com um rendimento constante.',
      },
      ingredients: INGREDIENTES_KETCHUP,
      formats: ['Cubo 5.000g'],
      formatDisplay: 'Cubo 5.000g',
      idealFor: {
        es: 'Cocinas industriales, comedores, caterings y establecimientos con alto volumen de preparación.',
        en: 'Industrial kitchens, canteens, caterers and establishments with high preparation volumes.',
        fr: 'Cuisines industrielles, cantines, traiteurs et établissements à fort volume de préparation.',
        pt: 'Cozinhas industriais, refeitórios, serviços de catering e estabelecimentos com elevado volume de preparação.',
      },
      productType: 'Horeca',
      image: '/images/productos/HORECA/Ketchup_cubo_5000gr.webp',
      formatImages: {
        'Cubo 5.000g': '/images/productos/HORECA/Ketchup_cubo_5000gr.webp'
      },
      bannerImage: '/images/productos/BANNERS IDEAL PARA/ketchup-normal-zero.webp'
    },
    {
      id: 'mostaza-dulce-cubo',
      name: 'Mostaza Dulce Cubo',
      shortName: 'Mostaza Dulce Cubo',
      category: {
        es: 'Formato Cubo',
        en: 'Bucket Format',
        fr: 'Format seau',
        pt: 'Formato balde',
      },
      categorySlug: 'cubo',
      shortDescription: {
        es: 'Mostaza versátil para hostelería en formato grande.',
        en: 'Versatile mustard for the hospitality trade in a large format.',
        fr: 'Moutarde polyvalente pour l’hôtellerie-restauration en grand format.',
        pt: 'Mostarda versátil para hotelaria e restauração em formato grande.',
      },
      description: {
        es: 'Mostaza dulce de textura suave y sabor equilibrado, ideal para hostelería y restauración que necesitan una salsa versátil y en formato grande.',
        en: 'Sweet mustard with a smooth texture and balanced flavour, ideal for hotels, bars and restaurants that need a versatile sauce in a large format.',
        fr: 'Moutarde sucrée à la texture douce et à la saveur équilibrée, idéale pour l’hôtellerie-restauration qui a besoin d’une sauce polyvalente en grand format.',
        pt: 'Mostarda doce de textura suave e sabor equilibrado, ideal para a hotelaria e restauração que precisam de um molho versátil e em formato grande.',
      },
      ingredients: {
        es: 'Agua, azúcar, vinagre de alcohol, harina de mostaza, aceite de girasol, sal, especias, estabilizantes (goma guar, goma xantana, goma garrofín, pectinas), aromas (apio) y conservadores (benzoato de sodio y sorbato de potasio).',
        en: 'Water, sugar, spirit vinegar, mustard flour, sunflower oil, salt, spices, stabilisers (guar gum, xanthan gum, locust bean gum, pectins), flavourings (celery) and preservatives (sodium benzoate and potassium sorbate).',
        fr: 'Eau, sucre, vinaigre d’alcool, farine de moutarde, huile de tournesol, sel, épices, stabilisants (gomme guar, gomme xanthane, gomme de caroube, pectines), arômes (céleri) et conservateurs (benzoate de sodium et sorbate de potassium).',
        pt: 'Água, açúcar, vinagre de álcool, farinha de mostarda, óleo de girassol, sal, especiarias, estabilizadores (goma guar, goma xantana, goma de alfarroba, pectinas), aromas (aipo) e conservantes (benzoato de sódio e sorbato de potássio).',
      },
      formats: ['Cubo 5.000g'],
      formatDisplay: 'Cubo 5.000g',
      idealFor: {
        es: 'Restaurantes, bares, áreas de servicio y negocios de comida rápida.',
        en: 'Restaurants, bars, service areas and fast-food businesses.',
        fr: 'Restaurants, bars, aires de service et commerces de restauration rapide.',
        pt: 'Restaurantes, bares, áreas de serviço e negócios de comida rápida.',
      },
      productType: 'Horeca',
      image: '/images/productos/HORECA/Mostaza_dulce_cubo_5000g.webp',
      bannerImage: '/images/productos/BANNERS IDEAL PARA/mostaza-dulce.webp'
    },
    {
      id: 'barbacoa-cubo',
      name: 'Salsa Barbacoa Cubo',
      shortName: 'Barbacoa Cubo',
      category: {
        es: 'Formato Cubo',
        en: 'Bucket Format',
        fr: 'Format seau',
        pt: 'Formato balde',
      },
      categorySlug: 'cubo',
      shortDescription: {
        es: 'Barbacoa ahumada estable para gran volumen.',
        en: 'Stable smoky barbecue sauce for large volumes.',
        fr: 'Sauce barbecue fumée stable pour grands volumes.',
        pt: 'Molho barbecue fumado estável para grande volume.',
      },
      description: {
        es: 'Salsa barbacoa con notas ahumadas y un sabor intenso, formulada para cocinas profesionales que buscan una salsa estable y lista para usar en gran volumen.',
        en: 'Barbecue sauce with smoky notes and an intense flavour, formulated for professional kitchens that want a stable, ready-to-use sauce in large volumes.',
        fr: 'Sauce barbecue aux notes fumées et à la saveur intense, formulée pour les cuisines professionnelles qui recherchent une sauce stable et prête à l’emploi en grand volume.',
        pt: 'Molho barbecue com notas fumadas e um sabor intenso, formulado para cozinhas profissionais que procuram um molho estável e pronto a usar em grande volume.',
      },
      ingredients: INGREDIENTES_BARBACOA,
      formats: ['Cubo 5.000g'],
      formatDisplay: 'Cubo 5.000g',
      idealFor: {
        es: 'Parrillas, hamburgueserías, caterings y establecimientos de comida americana o tex-mex.',
        en: 'Grill houses, burger restaurants, caterers and American or Tex-Mex food establishments.',
        fr: 'Grillades, hamburgeries, traiteurs et établissements de cuisine américaine ou tex-mex.',
        pt: 'Grelhados, hamburguerias, serviços de catering e estabelecimentos de comida americana ou tex-mex.',
      },
      productType: 'Horeca',
      image: '/images/productos/HORECA/Barbacoa_cubo_5000g.webp',
      formatImages: {
        'Cubo 5.000g': '/images/productos/HORECA/Barbacoa_cubo_5000g.webp'
      },
      bannerImage: '/images/productos/BANNERS IDEAL PARA/Barbacoa.webp'
    }
  ],

  // CATEGORÍA 4: FORMATO LATA
  lata: [
    {
      id: 'ketchup-lata',
      name: 'Ketchup Intercasa Lata',
      shortName: 'Ketchup Lata',
      category: {
        es: 'Formato Lata',
        en: 'Can Format',
        fr: 'Format boîte',
        pt: 'Formato lata',
      },
      categorySlug: 'lata',
      shortDescription: {
        es: 'Ketchup elaborado con tomates seleccionados para cocinas profesionales.',
        en: 'Ketchup made from selected tomatoes for professional kitchens.',
        fr: 'Ketchup élaboré avec des tomates sélectionnées pour les cuisines professionnelles.',
        pt: 'Ketchup elaborado com tomates selecionados para cozinhas profissionais.',
      },
      description: {
        es: 'Ketchup elaborado con tomates seleccionados y un sabor para cocinas profesionales.',
        en: 'Ketchup made from selected tomatoes with a flavour suited to professional kitchens.',
        fr: 'Ketchup élaboré avec des tomates sélectionnées, à la saveur adaptée aux cuisines professionnelles.',
        pt: 'Ketchup elaborado com tomates selecionados e um sabor pensado para cozinhas profissionais.',
      },
      ingredients: {
        es: 'Concentrado de tomate, azúcar, vinagre de alcohol, sal, almidón modificado de maíz, estabilizantes (goma guar), conservador (benzoato sódico) y aromas.',
        en: 'Tomato concentrate, sugar, spirit vinegar, salt, modified maize starch, stabilisers (guar gum), preservative (sodium benzoate) and flavourings.',
        fr: 'Concentré de tomate, sucre, vinaigre d’alcool, sel, amidon modifié de maïs, stabilisants (gomme guar), conservateur (benzoate de sodium) et arômes.',
        pt: 'Concentrado de tomate, açúcar, vinagre de álcool, sal, amido modificado de milho, estabilizadores (goma guar), conservante (benzoato de sódio) e aromas.',
      },
      formats: ['Lata 4.350g'],
      formatDisplay: 'Lata 4.350g',
      idealFor: {
        es: 'Cocinas industriales, restaurantes, comedores colectivos y servicios de restauración con alto consumo.',
        en: 'Industrial kitchens, restaurants, collective canteens and catering services with high consumption.',
        fr: 'Cuisines industrielles, restaurants, restauration collective et services de restauration à forte consommation.',
        pt: 'Cozinhas industriais, restaurantes, refeitórios coletivos e serviços de restauração com elevado consumo.',
      },
      productType: 'Horeca',
      image: '/images/productos/HORECA/Ketchup_lata_4350g.webp',
      bannerImage: '/images/productos/BANNERS IDEAL PARA/ketchup-normal-zero.webp'
    },
    {
      id: 'salsa-pizza-lata',
      name: 'Salsa Pizza Lata',
      shortName: 'Salsa Pizza Lata',
      category: {
        es: 'Formato Lata',
        en: 'Can Format',
        fr: 'Format boîte',
        pt: 'Formato lata',
      },
      categorySlug: 'lata',
      shortDescription: {
        es: 'Salsa de tomate diseñada para pizzas con textura homogénea.',
        en: 'Tomato sauce designed for pizzas, with an even texture.',
        fr: 'Sauce tomate conçue pour les pizzas, à la texture homogène.',
        pt: 'Molho de tomate concebido para pizzas, com textura homogénea.',
      },
      description: {
        es: 'Salsa de tomate diseñada para pizzas, con textura homogénea y un sabor equilibrado.',
        en: 'Tomato sauce designed for pizzas, with an even texture and a balanced flavour.',
        fr: 'Sauce tomate conçue pour les pizzas, à la texture homogène et à la saveur équilibrée.',
        pt: 'Molho de tomate concebido para pizzas, com textura homogénea e sabor equilibrado.',
      },
      ingredients: {
        es: 'Tomate, almidón de maíz modificado, sal, azúcar, aceite de girasol, acidulante: ácido cítrico, cebolla, albahaca y orégano.',
        en: 'Tomato, modified maize starch, salt, sugar, sunflower oil, acidifier: citric acid, onion, basil and oregano.',
        fr: 'Tomate, amidon de maïs modifié, sel, sucre, huile de tournesol, acidifiant : acide citrique, oignon, basilic et origan.',
        pt: 'Tomate, amido de milho modificado, sal, açúcar, óleo de girassol, acidificante: ácido cítrico, cebola, manjericão e orégão.',
      },
      formats: ['Lata 2.550g'],
      formatDisplay: 'Lata 2.550g',
      idealFor: {
        es: 'Pizzerías, cocinas profesionales, hornos industriales y establecimientos especializados en pizza.',
        en: 'Pizzerias, professional kitchens, industrial ovens and pizza specialists.',
        fr: 'Pizzerias, cuisines professionnelles, fours industriels et établissements spécialisés dans la pizza.',
        pt: 'Pizzarias, cozinhas profissionais, fornos industriais e estabelecimentos especializados em pizza.',
      },
      productType: 'Horeca',
      image: '/images/productos/HORECA/Salsa_pizza_lata_2550g.webp',
      bannerImage: '/images/productos/BANNERS IDEAL PARA/ketchup-normal-zero.webp'
    },
    {
      id: 'tomate-frito-lata',
      name: 'Tomate Frito Intercasa Lata',
      shortName: 'Tomate Frito Lata',
      category: {
        es: 'Formato Lata',
        en: 'Can Format',
        fr: 'Format boîte',
        pt: 'Formato lata',
      },
      categorySlug: 'lata',
      shortDescription: {
        es: 'Tomate frito listo para usar en cocinas profesionales.',
        en: 'Ready-to-use fried tomato sauce for professional kitchens.',
        fr: 'Sauce tomate prête à l’emploi pour les cuisines professionnelles.',
        pt: 'Tomate frito pronto a usar em cozinhas profissionais.',
      },
      description: {
        es: 'Tomate frito listo para usar para cocinas profesionales.',
        en: 'Ready-to-use fried tomato sauce for professional kitchens.',
        fr: 'Sauce tomate prête à l’emploi pour les cuisines professionnelles.',
        pt: 'Tomate frito pronto a usar para cozinhas profissionais.',
      },
      ingredients: {
        es: 'Agua, concentrado de tomate, almidón modificado de maíz, aceite de girasol, azúcar, sal, cebolla molida, ajo molido, conservador (benzoato sódico), antioxidante (ácido cítrico) y zumo de zanahoria negra.',
        en: 'Water, tomato concentrate, modified maize starch, sunflower oil, sugar, salt, ground onion, ground garlic, preservative (sodium benzoate), antioxidant (citric acid) and black carrot juice.',
        fr: 'Eau, concentré de tomate, amidon modifié de maïs, huile de tournesol, sucre, sel, oignon moulu, ail moulu, conservateur (benzoate de sodium), antioxydant (acide citrique) et jus de carotte noire.',
        pt: 'Água, concentrado de tomate, amido modificado de milho, óleo de girassol, açúcar, sal, cebola moída, alho moído, conservante (benzoato de sódio), antioxidante (ácido cítrico) e sumo de cenoura preta.',
      },
      formats: ['Lata 2.550g'],
      formatDisplay: 'Lata 2.550g',
      idealFor: {
        es: 'Restaurantes, comedores, caterings y preparaciones de salsas, guisos y platos elaborados.',
        en: 'Restaurants, canteens, caterers and preparations of sauces, stews and prepared dishes.',
        fr: 'Restaurants, cantines, traiteurs et préparations de sauces, ragoûts et plats cuisinés.',
        pt: 'Restaurantes, refeitórios, serviços de catering e preparação de molhos, guisados e pratos elaborados.',
      },
      productType: 'Horeca',
      image: '/images/productos/HORECA/Tomate_frito_lata_2550g.webp',
      bannerImage: '/images/productos/BANNERS IDEAL PARA/ketchup-normal-zero.webp'
    },
    {
      id: 'concentrado-tomate-lata',
      name: 'Doble Concentrado de Tomate Lata',
      shortName: 'Doble Concentrado Lata',
      category: {
        es: 'Formato Lata',
        en: 'Can Format',
        fr: 'Format boîte',
        pt: 'Formato lata',
      },
      categorySlug: 'lata',
      shortDescription: {
        es: 'Concentrado de tomate para reforzar color y sabor en recetas.',
        en: 'Tomato concentrate to boost colour and flavour in recipes.',
        fr: 'Concentré de tomate pour renforcer la couleur et la saveur des recettes.',
        pt: 'Concentrado de tomate para reforçar a cor e o sabor das receitas.',
      },
      description: {
        es: 'Concentrado de tomate para reforzar color y sabor en recetas profesionales.',
        en: 'Tomato concentrate to boost colour and flavour in professional recipes.',
        fr: 'Concentré de tomate pour renforcer la couleur et la saveur des recettes professionnelles.',
        pt: 'Concentrado de tomate para reforçar a cor e o sabor em receitas profissionais.',
      },
      ingredients: {
        es: 'Tomates, sal y ácido cítrico.',
        en: 'Tomatoes, salt and citric acid.',
        fr: 'Tomates, sel et acide citrique.',
        pt: 'Tomates, sal e ácido cítrico.',
      },
      formats: ['Lata 400g', 'Lata 4.540g'],
      formatDisplay: 'Lata 400g | 4.540g',
      idealFor: {
        es: 'Cocinas profesionales, salsas, fondos, guisos y elaboraciones que necesitan un punto de tomate más intenso.',
        en: 'Professional kitchens, sauces, stocks, stews and dishes that need a more intense tomato note.',
        fr: 'Cuisines professionnelles, sauces, fonds, ragoûts et préparations qui demandent une note de tomate plus intense.',
        pt: 'Cozinhas profissionais, molhos, fundos, guisados e preparações que precisam de um toque de tomate mais intenso.',
      },
      productType: 'Horeca',
      image: '/images/productos/HORECA/Concentrado_tomate_lata_4540g.webp',
      formatImages: {
        'Lata 400g': '/images/productos/HORECA/Concentrado_tomate_lata_4540g.webp',
        'Lata 4.540g': '/images/productos/HORECA/Concentrado_tomate_lata_4540g.webp'
      },
      bannerImage: '/images/productos/BANNERS IDEAL PARA/ketchup-normal-zero.webp'
    },
    {
      id: 'tomate-pelado-lata',
      name: 'Tomate Entero Pelado Lata',
      shortName: 'Tomate Pelado Lata',
      category: {
        es: 'Formato Lata',
        en: 'Can Format',
        fr: 'Format boîte',
        pt: 'Formato lata',
      },
      categorySlug: 'lata',
      shortDescription: {
        es: 'Tomates enteros pelados conservados en su jugo.',
        en: 'Whole peeled tomatoes preserved in their juice.',
        fr: 'Tomates entières pelées conservées dans leur jus.',
        pt: 'Tomates inteiros pelados conservados no seu sumo.',
      },
      description: {
        es: 'Tomates enteros pelados y conservados en su jugo para recetas profesionales.',
        en: 'Whole peeled tomatoes preserved in their own juice for professional recipes.',
        fr: 'Tomates entières pelées et conservées dans leur jus pour les recettes professionnelles.',
        pt: 'Tomates inteiros pelados e conservados no seu sumo para receitas profissionais.',
      },
      ingredients: {
        es: 'Tomate, jugo de tomate, sal, acidulante: ácido cítrico y endurecedor: cloruro cálcico.',
        en: 'Tomato, tomato juice, salt, acidifier: citric acid and firming agent: calcium chloride.',
        fr: 'Tomate, jus de tomate, sel, acidifiant : acide citrique et raffermissant : chlorure de calcium.',
        pt: 'Tomate, sumo de tomate, sal, acidificante: ácido cítrico e endurecedor: cloreto de cálcio.',
      },
      formats: ['Lata 4.000g'],
      formatDisplay: 'Lata 4.000g',
      idealFor: {
        es: 'Restaurantes, pizzerías, cocinas industriales y elaboraciones que parten del tomate entero.',
        en: 'Restaurants, pizzerias, industrial kitchens and dishes that start from whole tomatoes.',
        fr: 'Restaurants, pizzerias, cuisines industrielles et préparations à base de tomates entières.',
        pt: 'Restaurantes, pizzarias, cozinhas industriais e preparações que partem do tomate inteiro.',
      },
      productType: 'Horeca',
      image: '/images/productos/HORECA/Tomate_pelado_lata_4000g.webp',
      bannerImage: '/images/productos/BANNERS IDEAL PARA/ketchup-normal-zero.webp'
    },
    {
      id: 'tomate-triturado-lata',
      name: 'Tomate Triturado Lata',
      shortName: 'Tomate Triturado Lata',
      category: {
        es: 'Formato Lata',
        en: 'Can Format',
        fr: 'Format boîte',
        pt: 'Formato lata',
      },
      categorySlug: 'lata',
      shortDescription: {
        es: 'Tomate triturado de textura uniforme para salsas y guisos.',
        en: 'Crushed tomato with a uniform texture for sauces and stews.',
        fr: 'Tomate concassée à la texture uniforme pour sauces et ragoûts.',
        pt: 'Tomate triturado de textura uniforme para molhos e guisados.',
      },
      description: {
        es: 'Tomate triturado de textura uniforme, ideal como base para salsas, guisos y recetas profesionales de gran volumen.',
        en: 'Crushed tomato with a uniform texture, ideal as a base for sauces, stews and high-volume professional recipes.',
        fr: 'Tomate concassée à la texture uniforme, idéale comme base pour les sauces, les ragoûts et les recettes professionnelles en grand volume.',
        pt: 'Tomate triturado de textura uniforme, ideal como base para molhos, guisados e receitas profissionais de grande volume.',
      },
      ingredients: {
        es: 'Tomate, sal y acidulante: ácido cítrico.',
        en: 'Tomato, salt and acidifier: citric acid.',
        fr: 'Tomate, sel et acidifiant : acide citrique.',
        pt: 'Tomate, sal e acidificante: ácido cítrico.',
      },
      formats: ['Lata 4.000g'],
      formatDisplay: 'Lata 4.000g',
      idealFor: {
        es: 'Cocinas industriales, restaurantes, caterings y preparaciones de salsas y fondos.',
        en: 'Industrial kitchens, restaurants, caterers and preparations of sauces and stocks.',
        fr: 'Cuisines industrielles, restaurants, traiteurs et préparations de sauces et de fonds.',
        pt: 'Cozinhas industriais, restaurantes, serviços de catering e preparação de molhos e fundos.',
      },
      productType: 'Horeca',
      image: '/images/productos/HORECA/Tomate_triturado_lata_4000g.webp',
      bannerImage: '/images/productos/BANNERS IDEAL PARA/ketchup-normal-zero.webp'
    }
  ],

  // CATEGORÍA 5: MAYONESAS Y ALIOLI EN CUBO
  mayonesasAlioli: [
    {
      id: 'mayonesa-cubo',
      name: 'Mayonesa Intercasa Cubo',
      shortName: 'Mayonesa Cubo',
      category: {
        es: 'Mayonesas y Alioli en Cubo',
        en: 'Mayonnaise and Alioli in Buckets',
        fr: 'Mayonnaises et alioli en seau',
        pt: 'Maioneses e alioli em balde',
      },
      categorySlug: 'mayonesas-alioli',
      shortDescription: {
        es: 'Mayonesa versátil de gran rendimiento profesional.',
        en: 'Versatile, high-yield mayonnaise for professional use.',
        fr: 'Mayonnaise polyvalente au rendement professionnel élevé.',
        pt: 'Maionese versátil de elevado rendimento profissional.',
      },
      description: {
        es: 'Mayonesa de textura suave y sabor equilibrado, ideal para cocinas profesionales que necesitan un producto versátil y de gran rendimiento.',
        en: 'Mayonnaise with a smooth texture and balanced flavour, ideal for professional kitchens that need a versatile, high-yield product.',
        fr: 'Mayonnaise à la texture douce et à la saveur équilibrée, idéale pour les cuisines professionnelles qui ont besoin d’un produit polyvalent et à haut rendement.',
        pt: 'Maionese de textura suave e sabor equilibrado, ideal para cozinhas profissionais que precisam de um produto versátil e de elevado rendimento.',
      },
      ingredients: {
        es: 'Aceite de girasol (64%), agua, huevo y yema de huevo (6,5%), vinagre de vino blanco, azúcar, sal, almidón modificado de maíz, estabilizante (goma garrofín), zumo de limón, conservador (sorbato potásico), mostaza, acidulante (ácido cítrico), antioxidante (EDTA), colorante (betacaroteno) y aromas.',
        en: 'Sunflower oil (64%), water, egg and egg yolk (6.5%), white wine vinegar, sugar, salt, modified maize starch, stabiliser (locust bean gum), lemon juice, preservative (potassium sorbate), mustard, acidifier (citric acid), antioxidant (EDTA), colour (beta-carotene) and flavourings.',
        fr: 'Huile de tournesol (64\u00a0%), eau, œuf et jaune d’œuf (6,5\u00a0%), vinaigre de vin blanc, sucre, sel, amidon modifié de maïs, stabilisant (gomme de caroube), jus de citron, conservateur (sorbate de potassium), moutarde, acidifiant (acide citrique), antioxydant (EDTA), colorant (bêta-carotène) et arômes.',
        pt: 'Óleo de girassol (64%), água, ovo e gema de ovo (6,5%), vinagre de vinho branco, açúcar, sal, amido modificado de milho, estabilizador (goma de alfarroba), sumo de limão, conservante (sorbato de potássio), mostarda, acidificante (ácido cítrico), antioxidante (EDTA), corante (betacaroteno) e aromas.',
      },
      formats: ['Cubo 5.000ml', 'Cubo 10.000ml'],
      formatDisplay: 'Cubo 5.000ml | 10.000ml',
      idealFor: {
        es: 'Caterings, cocinas industriales, comedores, obradores y establecimientos con alto volumen de producción.',
        en: 'Caterers, industrial kitchens, canteens, production kitchens and establishments with high production volumes.',
        fr: 'Traiteurs, cuisines industrielles, cantines, laboratoires de production et établissements à fort volume de production.',
        pt: 'Serviços de catering, cozinhas industriais, refeitórios, unidades de produção e estabelecimentos com elevado volume de produção.',
      },
      productType: 'Horeca',
      image: '/images/productos/HORECA/Mayonesa_cubo_5000ml.webp',
      formatImages: {
        'Cubo 5.000ml': '/images/productos/HORECA/Mayonesa_cubo_5000ml.webp',
        'Cubo 10.000ml': '/images/productos/HORECA/Mayonesa_cubo_10000ml.webp'
      },
      bannerImage: '/images/productos/BANNERS IDEAL PARA/Mayonesa.webp'
    },
    {
      id: 'mayonesa-ligera-cubo',
      name: 'Mayonesa Ligera Cubo',
      shortName: 'Mayonesa Ligera Cubo',
      category: {
        es: 'Mayonesas y Alioli en Cubo',
        en: 'Mayonnaise and Alioli in Buckets',
        fr: 'Mayonnaises et alioli en seau',
        pt: 'Maioneses e alioli em balde',
      },
      categorySlug: 'mayonesas-alioli',
      shortDescription: {
        es: 'Opción ligera para profesionales sin perder calidad.',
        en: 'A lighter option for professionals without losing quality.',
        fr: 'Une option plus légère pour les professionnels, sans perte de qualité.',
        pt: 'Opção leve para profissionais sem perder qualidade.',
      },
      description: {
        es: 'Versión ligera de nuestra mayonesa clásica, con textura suave y un sabor equilibrado, pensada para profesionales que buscan una opción más ligera sin perder calidad.',
        en: 'A light version of our classic mayonnaise, with a smooth texture and balanced flavour, made for professionals looking for a lighter option without losing quality.',
        fr: 'Version allégée de notre mayonnaise classique, à la texture douce et à la saveur équilibrée, pensée pour les professionnels qui recherchent une option plus légère sans perdre en qualité.',
        pt: 'Versão leve da nossa maionese clássica, com textura suave e sabor equilibrado, pensada para profissionais que procuram uma opção mais leve sem perder qualidade.',
      },
      ingredients: {
        es: 'Agua, Aceite de Soja genéticamente modificada (30%), Vinagre, Almidón modificado de maíz, Azúcar, Sal, Huevo (0,85%), Estabilizantes (goma xantana), Zumo de limón, Conservador (benzoato sódico), Acidulante (ácido cítrico), Antioxidante (EDTA), Colorante (Betacaroteno) y Aromas.',
        en: 'Water, Genetically modified soya oil (30%), Vinegar, Modified maize starch, Sugar, Salt, Egg (0.85%), Stabilisers (xanthan gum), Lemon juice, Preservative (sodium benzoate), Acidifier (citric acid), Antioxidant (EDTA), Colour (Beta-carotene) and Flavourings.',
        fr: 'Eau, Huile de soja génétiquement modifiée (30\u00a0%), Vinaigre, Amidon modifié de maïs, Sucre, Sel, Œuf (0,85\u00a0%), Stabilisants (gomme xanthane), Jus de citron, Conservateur (benzoate de sodium), Acidifiant (acide citrique), Antioxydant (EDTA), Colorant (Bêta-carotène) et Arômes.',
        pt: 'Água, Óleo de soja geneticamente modificado (30%), Vinagre, Amido modificado de milho, Açúcar, Sal, Ovo (0,85%), Estabilizadores (goma xantana), Sumo de limão, Conservante (benzoato de sódio), Acidificante (ácido cítrico), Antioxidante (EDTA), Corante (Betacaroteno) e Aromas.',
      },
      formats: ['Cubo 10.000ml'],
      formatDisplay: 'Cubo 10.000ml',
      idealFor: {
        es: 'Cocinas profesionales, caterings y negocios que buscan alternativas más ligeras para su menú.',
        en: 'Professional kitchens, caterers and businesses looking for lighter alternatives for their menu.',
        fr: 'Cuisines professionnelles, traiteurs et établissements qui recherchent des alternatives plus légères pour leur carte.',
        pt: 'Cozinhas profissionais, serviços de catering e negócios que procuram alternativas mais leves para a sua ementa.',
      },
      productType: 'Horeca',
      image: '/images/productos/HORECA/Salsa_fina_cubo_10000ml.webp',
      bannerImage: '/images/productos/BANNERS IDEAL PARA/Mayonesa.webp'
    },
    {
      id: 'alioli-receta-canaria-cubo',
      name: 'Alioli Receta Canaria Cubo',
      shortName: 'Alioli Receta Canaria Cubo',
      category: {
        es: 'Mayonesas y Alioli en Cubo',
        en: 'Mayonnaise and Alioli in Buckets',
        fr: 'Mayonnaises et alioli en seau',
        pt: 'Maioneses e alioli em balde',
      },
      categorySlug: 'mayonesas-alioli',
      shortDescription: {
        es: 'Alioli auténtico canario para hostelería.',
        en: 'Authentic Canarian alioli for the hospitality trade.',
        fr: 'Alioli canarien authentique pour l’hôtellerie-restauration.',
        pt: 'Alioli canário autêntico para hotelaria e restauração.',
      },
      description: {
        es: 'Alioli de receta canaria con un sabor característico, perfecto para acompañar carnes, papas y platos tradicionales, adaptado al uso profesional en hostelería.',
        en: 'Alioli (garlic mayonnaise) made to a Canarian recipe with a distinctive flavour, perfect for accompanying meat, potatoes and traditional dishes, adapted for professional use in the hospitality trade.',
        fr: 'Alioli (mayonnaise à l’ail) de recette canarienne à la saveur caractéristique, parfait pour accompagner viandes, pommes de terre et plats traditionnels, adapté à un usage professionnel en hôtellerie-restauration.',
        pt: 'Alioli (maionese de alho) de receita canária com um sabor característico, perfeito para acompanhar carnes, batatas e pratos tradicionais, adaptado ao uso profissional na hotelaria e restauração.',
      },
      ingredients: INGREDIENTES_ALIOLI,
      formats: ['Cubo 1.850ml'],
      formatDisplay: 'Cubo 1.850ml',
      idealFor: {
        es: 'Asadores, restaurantes, bares, guachinches, caterings y locales que buscan un alioli auténtico y de sabor intenso.',
        en: 'Grill houses, restaurants, bars, guachinches (traditional Canarian eateries), caterers and venues looking for an authentic alioli with an intense flavour.',
        fr: 'Rôtisseries, restaurants, bars, guachinches (auberges traditionnelles canariennes), traiteurs et établissements qui recherchent un alioli authentique à la saveur intense.',
        pt: 'Assadores, restaurantes, bares, guachinches (tascas tradicionais canárias), serviços de catering e estabelecimentos que procuram um alioli autêntico e de sabor intenso.',
      },
      productType: 'Horeca',
      image: '/images/productos/HORECA/Alioli_cubo_1850ml.webp',
      bannerImage: '/images/productos/BANNERS IDEAL PARA/Alioli.webp'
    }
  ]
};

/**
 * Obtiene todos los productos horeca en un array plano
 * @returns {Array} Array con todos los productos
 */
export const getAllHorecaProducts = () => {
  return [
    ...horecaProductsData.biberon,
    ...horecaProductsData.garrafa,
    ...horecaProductsData.cubo,
    ...horecaProductsData.lata,
    ...horecaProductsData.mayonesasAlioli
  ];
};

/**
 * Busca un producto horeca por su ID
 * @param {string} productId - ID del producto
 * @returns {Object|null} Producto encontrado o null
 */
export const getHorecaProductById = (productId) => {
  const allProducts = getAllHorecaProducts();
  return allProducts.find(product => product.id === productId) || null;
};

/**
 * Obtiene productos horeca por categoría
 * @param {string} categorySlug - Slug de la categoría
 * @returns {Array} Array de productos de la categoría
 */
export const getHorecaProductsByCategory = (categorySlug) => {
  const categoryMap = {
    'biberon': horecaProductsData.biberon,
    'garrafa': horecaProductsData.garrafa,
    'cubo': horecaProductsData.cubo,
    'lata': horecaProductsData.lata,
    'mayonesas-alioli': horecaProductsData.mayonesasAlioli
  };
  return categoryMap[categorySlug] || [];
};